import { useCallback } from 'react'

import { OptionalPayload, RequiredPayload } from '@bdc-libs/matrix.web-tracking'

import { useAuth } from '@sputnik/auth'
import { CollaboratorRole, useApplicationCollaboratorsLazy } from '@sputnik/hooks/useApplicationCollaborators'
import { WebTrackingPlan } from '@sputnik/tracking/webtracking'
import { useWebTracking } from '@sputnik/tracking/WebTrackingContext'

type RequiredPayloadType<T> = T extends RequiredPayload ? Omit<T, '__required'> : T
type OptionalPayloadType<T> = T extends OptionalPayload ? Omit<T, '__optional'> : T

type TrackMethodArgs<T extends keyof Plan, Plan> = Plan[T] extends undefined
  ? [event: T]
  : Plan[T] extends RequiredPayload
    ? [event: T, properties: (role: CollaboratorRole) => RequiredPayloadType<Plan[T]>]
    : Plan[T] extends OptionalPayload
      ? [event: T, properties?: (role: CollaboratorRole) => OptionalPayloadType<Plan[T]>]
      : [event: T, properties: (role: CollaboratorRole) => Plan[T]]

export interface UseTrackCollaboratorEventProps {
  salesforceCreditId: string
}

export const useTrackCollaboratorEvent = ({ salesforceCreditId }: UseTrackCollaboratorEventProps) => {
  const { email } = useAuth()
  const { track } = useWebTracking()

  const { getApplicationCollaborators } = useApplicationCollaboratorsLazy({ salesforceCreditId })

  const trackCollaboratorEvent = useCallback(
    async <T extends keyof WebTrackingPlan>(...args: TrackMethodArgs<T, WebTrackingPlan>) => {
      const collaborators = await getApplicationCollaborators()

      if (collaborators.length < 1) {
        return
      }

      const collaboratorRecord = collaborators.find(({ email: collabEmail }) => collabEmail === email)
      if (!collaboratorRecord || !collaboratorRecord.role) {
        return
      }
      const [event, properties] = args

      //@ts-ignore
      return track(event, properties?.(collaboratorRecord.role))
    },
    [email, getApplicationCollaborators, track],
  )

  return { trackCollaboratorEvent }
}
